import { SHAPE_STROKE_PROPS } from './stroke-props';

interface PillProps {
  strokeDasharray: string | undefined;
  /**
   * Real rendered node dimensions in pixels. Used ONLY to pre-compensate the
   * corner radius for the parent <svg>'s non-uniform stretch — NOT to change
   * the viewBox. The rect is always drawn in the shared static 0 0 100 100
   * box like every other variant; giving the parent a per-node viewBox caused
   * a ResizeObserver measurement feedback loop (canvas freeze on load).
   * Absent in dimensionless contexts (e.g. the picker icon) → square circle.
   */
  width?: number;
  height?: number;
}

const BOX = 100;

/**
 * Pill / stadium / discorectangle — a rectangle whose two short ends are
 * full semicircles. Drawn in the static 100×100 viewBox; the parent stretches
 * it to W×H with preserveAspectRatio="none". To keep the caps circular we
 * pre-divide the pixel cap radius by each axis's scale:
 *   rendered cap r = min(W,H)/2 px ⇒ rx = r*100/W, ry = r*100/H
 * so rx*(W/100) == ry*(H/100) == r. With no dims it's a circle (rx=ry=50).
 */
export default function Pill({ strokeDasharray, width, height }: PillProps) {
  const w = width && width > 0 ? width : BOX;
  const h = height && height > 0 ? height : BOX;
  const r = Math.min(w, h) / 2;
  const rx = Math.min(BOX / 2, (r * BOX) / w);
  const ry = Math.min(BOX / 2, (r * BOX) / h);
  return (
    <rect
      x="0" y="0" width={BOX} height={BOX}
      rx={rx} ry={ry}
      strokeDasharray={strokeDasharray}
      {...SHAPE_STROKE_PROPS}
    />
  );
}
